/*
 * <ajax-listing>: updates a book listing (filters, sort, pagination, search)
 * in place instead of loading a new page. Fetches only its own section via
 * Shopify's Section Rendering API (?section_id=), swaps the matching
 * <ajax-listing> in, and keeps the address bar, back and forward in step.
 * Falls back to a normal page load if anything goes wrong.
 */

if (!customElements.get('ajax-listing')) {
  class AjaxListing extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      if (!AjaxListing.popstateBound) {
        AjaxListing.popstateBound = true;
        window.addEventListener('popstate', () => {
          const listing = document.querySelector('ajax-listing');
          if (listing) listing.load(window.location.href, { push: false });
        });
      }
    }

    sameListing(url) {
      return url.origin === window.location.origin && url.pathname === window.location.pathname;
    }

    onClick(event) {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (!link.closest('.listing-pagination, .filter-chips, .listing-empty, [data-ajax-link]')) return;
      const url = new URL(link.href, window.location.href);
      if (!this.sameListing(url)) return;
      event.preventDefault();
      this.load(url.href, { scroll: Boolean(link.closest('.listing-pagination')) });
    }

    onSubmit(event) {
      const form = event.target.closest('form[data-ajax-search]');
      if (!form) return;
      event.preventDefault();
      const params = new URLSearchParams(new FormData(form));
      this.load(`${form.getAttribute('action')}?${params.toString()}`, { keepFocus: true });
    }

    async load(href, { push = true, scroll = false, keepFocus = false } = {}) {
      const url = new URL(href, window.location.href);
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.dataset.sectionId);

      const dialog = this.querySelector('dialog[open]');
      const active = document.activeElement;
      const activeKey = active && this.contains(active) ? this.keyFor(active) : null;

      this.controller?.abort();
      this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(fetchUrl, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('ajax-listing');
        if (!next) throw new Error('No listing in response');

        const swap = () => {
          this.innerHTML = next.innerHTML;
          if (next.dataset.pageTitle) document.title = next.dataset.pageTitle;
          if (dialog) this.querySelector('dialog')?.showModal();
          const restored = activeKey ? this.findByKey(activeKey) : null;
          if (restored && (dialog || keepFocus)) restored.focus({ preventScroll: true });
        };
        if (document.startViewTransition && !dialog && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          await document.startViewTransition(swap).updateCallbackDone;
        } else {
          swap();
        }

        if (push) window.history.pushState({ ajaxListing: true }, '', url.href);
        if (scroll) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
        this.announce();
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = url.href;
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    keyFor(element) {
      if (element.id) return `#${element.id}`;
      if (element.name) return `[name="${element.name}"][value="${element.value}"]`;
      return null;
    }

    findByKey(key) {
      try {
        return this.querySelector(key);
      } catch (error) {
        return null;
      }
    }

    announce() {
      let status = document.getElementById('AjaxListingStatus');
      if (!status) {
        status = Object.assign(document.createElement('p'), { id: 'AjaxListingStatus', className: 'visually-hidden' });
        status.setAttribute('role', 'status');
        document.body.append(status);
      }
      const count = this.querySelector('[data-listing-status]');
      status.textContent = count ? count.textContent.trim() : '';
    }
  }

  customElements.define('ajax-listing', AjaxListing);
}
